import { useId } from 'react';
import { usePersistedPanelState } from '../hooks/usePersistedPanelState.js';
import CommandPanel from './ui/CommandPanel.js';
import SectionHeader from './ui/SectionHeader.js';

// ExplainerSection v0.4.2 - collapsible "About this dashboard" panel.
// Defaults to COLLAPSED. Earlier versions defaulted to expanded so
// first-time visitors saw the architecture details up front, but the
// home page is now dense enough (Tier 0 strip, Active Issues card,
// Long-Term Issues, RSS carousel, Tier F card) that the explainer
// crowds the signal. Visitors who want it click 'expand'; everyone
// else lands on the live status surface immediately.
// Counts and cadences are the real production values (verified against
// /providers + the relevant wrangler.toml crons), not approximations —
// visitors trust a status site that's specific.
export default function ExplainerSection() {
  const [expanded, setExpanded] = usePersistedPanelState('panel:explainer', false);
  const contentId = useId();

  return (
    <CommandPanel>
      <SectionHeader
        title="ABOUT THIS DASHBOARD"
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
        contentId={contentId}
      />
      {expanded && (
        <div id={contentId} className="px-4 py-3 text-sm text-text leading-relaxed space-y-3">
          <p>
            <span className="text-accent font-mono">secdefcon</span> tracks live operational status
            for{' '}
            <strong className="text-text">119 internet infrastructure and SaaS providers</strong> —
            weighted toward the layer that most consumer-facing outages cascade from. Click any
            provider's <span className="font-mono text-accent">↗</span> icon to open its upstream
            status page in a new tab; click the <span className="font-mono text-warn">?</span> icon
            for what services it covers.
          </p>

          <div>
            <div className="text-[10px] tracking-[2px] text-white font-semibold mb-1">
              WHAT IT TRACKS
            </div>
            <ul className="text-xs text-muted list-disc ml-5 space-y-1">
              <li>
                <span className="text-text font-semibold">Tier 0 (15 providers)</span> — critical
                infrastructure (Cloudflare, AWS, Azure, GCP, Google Workspace, Akamai, Fastly,
                Stripe, Twilio, PayPal, Slack, etc.) rendered above the fold.
              </li>
              <li>
                <span className="text-text font-semibold">11 categories</span> — cloud (18),
                dev-tools (15), productivity (14), AI/ML (9), payments (9), social (9), gaming (8),
                observability (8), communications (7), streaming (4), healthcare (3). Tier 1 and
                Tier 2 providers merge inside each category card with a per-row T1/T2 badge.
              </li>
              <li>
                <span className="text-text font-semibold">Active Issues</span> — every provider
                currently degraded, sorted by tier then severity. Issues older than 24h drop to a
                separate Long-Term Issues card so a multi-day region outage doesn't crowd the
                day-to-day signal.
              </li>
              <li>
                <span className="text-text font-semibold">Manual overrides</span> — when a
                provider's own status page misrepresents reality (taken offline mid-outage, stuck on
                "all green" while broken), an operator can flag the outage with a custom Notice that
                renders as a distinct{' '}
                <span className="text-warn font-semibold">
                  MANUALLY FLAGGED — UPSTREAM STATUS PAGE DISPUTED
                </span>{' '}
                strip above the standard incident chrome.
              </li>
              <li>
                <span className="text-text font-semibold">Tier F (Untrustworthy)</span> — providers
                graded F for misrepresenting their service status are reclassified out of the main
                grid and surfaced only in a dedicated card at the bottom of the page. F grades carry
                a 1-year minimum lock-in. Click any Tier F entry to open a detail popup with the
                timestamped evidence log (post-mortems, news coverage).
              </li>
            </ul>
          </div>

          <div>
            <div className="text-[10px] tracking-[2px] text-white font-semibold mb-1">
              HOW IT WORKS
            </div>
            <ul className="text-xs text-muted list-disc ml-5 space-y-1">
              <li>
                <span className="text-text font-semibold">Polling</span> — every 60 seconds a worker
                re-fetches each Tier 0 provider's status feed (Atlassian Statuspage JSON, custom
                JSON, or RSS/Atom). Parsers extract a 0-3 severity{' '}
                <span className="font-mono">(none · degraded · major · down)</span> and a
                human-readable message; only changes are persisted.
              </li>
              <li>
                <span className="text-text font-semibold">Real-time updates</span> — this page
                subscribes to a Server-Sent Events stream, so transitions reach connected browsers
                within ~1 second of being detected. A 60-second fallback poll handles disconnect
                recovery.
              </li>
              <li>
                <span className="text-text font-semibold">Incident mode</span> - when any Tier 0
                provider crosses into degraded, a Durable Object accelerates polling on that
                provider to every 10 seconds. The mode disarms automatically after ~15 minutes of
                stable green.
              </li>
              <li>
                <span className="text-text font-semibold">Detected timestamps</span> — the "detected
                Xh ago" label tracks when an issue was first observed in the current streak.
                Severity escalations and upstream message edits within an active incident preserve
                the original detection time, so the timer reflects how long the user has been
                affected, not when the vendor last updated their copy.
              </li>
            </ul>
          </div>

          <div>
            <div className="text-[10px] tracking-[2px] text-white font-semibold mb-1">
              ADDITIONAL SIGNALS
            </div>
            <ul className="text-xs text-muted list-disc ml-5 space-y-1">
              <li>
                <span className="text-text font-semibold">Latency probes</span> - 15 Tier 0
                endpoints (Cloudflare, AWS, Azure, GCP, Google Workspace, Akamai, Fastly, iCloud,
                Oracle Cloud, IBM Cloud, Salesforce, Stripe, Twilio, PayPal, Slack) measured every 5
                minutes from 9 Cloudflare regions (WNAM, ENAM, WEUR, EEUR, APAC, OC, SAM, AFR, ME).
              </li>
              <li>
                <span className="text-text font-semibold">BGP events</span> — during incident mode a
                live ticker streams withdrawal and announcement events for major ASNs from RIPE RIS
                Live, refreshed every 5 seconds.
              </li>
              <li>
                <span className="text-text font-semibold">Recent updates</span> — aggregated RSS
                feed items across providers that publish them, newest first, capped at 10 entries
                per provider so a noisy feed can't dominate.
              </li>
            </ul>
          </div>

          <p className="text-xs text-dim pt-1">
            Hosted entirely on Cloudflare — Workers, Durable Objects, D1, KV, R2, Analytics Engine,
            Queues, and Pages. Public surface is read-only and CDN-cached; the admin dashboard is
            gated by Cloudflare Access. No tracking pixels, no third-party analytics.
          </p>
        </div>
      )}
    </CommandPanel>
  );
}
